<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="720px">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="100px"
    >
      <el-form-item label="套餐名称" prop="name">
        <el-input v-model="formData.name" maxlength="100" placeholder="请输入套餐名称" />
      </el-form-item>
      <el-form-item label="活动时间" prop="startAndEndTime">
        <el-date-picker
          v-model="formData.startAndEndTime"
          end-placeholder="结束时间"
          start-placeholder="开始时间"
          type="datetimerange"
          value-format="YYYY-MM-DD HH:mm:ss"
        />
      </el-form-item>
      <el-form-item label="一口价(元)" prop="packagePriceYuan">
        <el-input-number
          v-model="formData.packagePriceYuan"
          :min="0.01"
          :precision="2"
          :step="1"
          class="!w-240px"
        />
      </el-form-item>
      <el-form-item label="总份数" prop="totalCount">
        <el-input-number v-model="formData.totalCount" :min="1" :step="1" class="!w-240px" />
        <span class="text-gray-400 text-12px ml-8px">活动一共可售多少套</span>
      </el-form-item>
      <el-form-item label="每人限购" prop="userLimitCount">
        <el-input-number v-model="formData.userLimitCount" :min="1" :step="1" class="!w-240px" />
        <span class="text-gray-400 text-12px ml-8px">同一用户累计最多买几套</span>
      </el-form-item>
      <el-form-item label="主图" prop="picUrl">
        <UploadImg v-model="formData.picUrl" />
      </el-form-item>
      <el-form-item label="备注" prop="remark">
        <el-input v-model="formData.remark" type="textarea" />
      </el-form-item>
      <el-form-item label="子品" required>
        <div class="w-100%">
          <el-button class="mb-10px" type="primary" @click="spuSelectRef?.open()">
            添加商品 SKU
          </el-button>
          <el-table :data="formData.items" border size="small">
            <el-table-column label="SPU" min-width="90" prop="spuId" />
            <el-table-column label="SKU" min-width="90" prop="skuId" />
            <el-table-column label="商品" min-width="160" prop="spuName" show-overflow-tooltip />
            <el-table-column label="数量" min-width="120">
              <template #default="{ row }">
                <el-input-number v-model="row.count" :min="1" :step="1" />
              </template>
            </el-table-column>
            <el-table-column align="center" label="操作" width="80">
              <template #default="{ $index }">
                <el-button link type="danger" @click="formData.items.splice($index, 1)">
                  删除
                </el-button>
              </template>
            </el-table-column>
          </el-table>
          <div class="text-gray-400 text-12px mt-6px">至少 2 个不同子品（SKU）</div>
        </div>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
  <SpuSelect ref="spuSelectRef" :isSelectSku="true" @confirm="onSelectSpu" />
</template>

<script lang="ts" setup>
import { SpuSelect } from '../../components'
import * as PackageActivityApi from '@/api/mall/promotion/package/packageActivity'
import type { PackageActivityVO, PackageItemVO } from '@/api/mall/promotion/package/packageActivity'
import * as ProductSpuApi from '@/api/mall/product/spu'
import { fenToYuan, yuanToFen } from '@/utils'

defineOptions({ name: 'PromotionPackageActivityForm' })

const { t } = useI18n()
const message = useMessage()

const dialogVisible = ref(false)
const dialogTitle = ref('')
const formLoading = ref(false)
const formType = ref('')
const formRef = ref()
const spuSelectRef = ref()

const formData = ref<{
  id?: number
  name: string
  startAndEndTime: string[]
  packagePriceYuan: number
  totalCount: number
  userLimitCount: number
  picUrl: string
  remark: string
  items: PackageItemVO[]
}>({
  name: '',
  startAndEndTime: [],
  packagePriceYuan: 1,
  totalCount: 100,
  userLimitCount: 1,
  picUrl: '',
  remark: '',
  items: []
})

const formRules = reactive({
  name: [{ required: true, message: '套餐名称不能为空', trigger: 'blur' }],
  startAndEndTime: [{ required: true, message: '活动时间不能为空', trigger: 'change' }],
  packagePriceYuan: [{ required: true, message: '一口价不能为空', trigger: 'blur' }],
  totalCount: [{ required: true, message: '总份数不能为空', trigger: 'blur' }],
  userLimitCount: [{ required: true, message: '每人限购不能为空', trigger: 'blur' }]
})

const resetForm = () => {
  formData.value = {
    name: '',
    startAndEndTime: [],
    packagePriceYuan: 1,
    totalCount: 100,
    userLimitCount: 1,
    picUrl: '',
    remark: '',
    items: []
  }
  formRef.value?.resetFields()
}

const open = async (type: string, id?: number) => {
  dialogVisible.value = true
  dialogTitle.value = t('action.' + type)
  formType.value = type
  resetForm()
  if (!id) {
    return
  }
  formLoading.value = true
  try {
    const data = (await PackageActivityApi.getPackageActivity(id)) as PackageActivityVO
    formData.value = {
      id: data.id,
      name: data.name,
      startAndEndTime: [data.startTime as any, data.endTime as any],
      packagePriceYuan: Number(fenToYuan(data.packagePrice)),
      totalCount: data.totalCount || 100,
      userLimitCount: data.userLimitCount || 1,
      picUrl: data.picUrl || '',
      remark: data.remark || '',
      items: (data.items || []).map((it) => ({ ...it, count: it.count || 1 }))
    }
  } finally {
    formLoading.value = false
  }
}
defineExpose({ open })

const onSelectSpu = async (spuId: number, skuIds?: number[]) => {
  if (!skuIds?.length) {
    return
  }
  const spu = await ProductSpuApi.getSpu(spuId)
  for (const skuId of skuIds) {
    if (formData.value.items.some((i) => i.skuId === skuId)) {
      message.warning(`SKU ${skuId} 已添加`)
      continue
    }
    const sku = (spu.skus || []).find((s: any) => s.id === skuId)
    formData.value.items.push({
      spuId,
      skuId,
      count: 1,
      spuName: spu.name,
      picUrl: sku?.picUrl || spu.picUrl,
      price: sku?.price
    })
  }
}

const emit = defineEmits(['success'])
const submitForm = async () => {
  await formRef.value.validate()
  if (!formData.value.items || formData.value.items.length < 2) {
    message.error('套餐至少包含 2 个子品')
    return
  }
  if (formData.value.userLimitCount > formData.value.totalCount) {
    message.error('每人限购不能大于总份数')
    return
  }
  formLoading.value = true
  try {
    const payload: PackageActivityVO = {
      id: formData.value.id,
      name: formData.value.name,
      startTime: formData.value.startAndEndTime[0],
      endTime: formData.value.startAndEndTime[1],
      packagePrice: yuanToFen(formData.value.packagePriceYuan),
      totalCount: formData.value.totalCount,
      userLimitCount: formData.value.userLimitCount,
      picUrl: formData.value.picUrl,
      remark: formData.value.remark,
      items: formData.value.items.map((i) => ({
        spuId: i.spuId,
        skuId: i.skuId,
        count: i.count
      }))
    }
    if (formType.value === 'create') {
      await PackageActivityApi.createPackageActivity(payload)
      message.success(t('common.createSuccess'))
    } else {
      await PackageActivityApi.updatePackageActivity(payload)
      message.success(t('common.updateSuccess'))
    }
    dialogVisible.value = false
    emit('success')
  } finally {
    formLoading.value = false
  }
}
</script>
